/**
 * Date handling without the JS Date timezone trap.
 *
 * Dates are ISO strings: `IsoDate` is "yyyy-MM-dd", `IsoDateTime` is "yyyy-MM-ddTHH:mm:ss" (the fixed width the
 * SQLite layer also writes). All arithmetic is date-only in UTC, so DST changes can never shift a day count.
 */
export type IsoDate = string;
export type IsoDateTime = string;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whole days from `from` to `to` (negative if `to` is earlier). Equivalent to Java ChronoUnit.DAYS.between. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((epochDay(to) - epochDay(from)));
}

/** Days since 1970-01-01 for a date-only ISO string. */
export function epochDay(date: IsoDate): number {
  const m = DATE_RE.exec(date);
  if (!m) throw new RangeError(`not an ISO date: ${date}`);
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  return Date.UTC(y, mo - 1, d) / 86_400_000;
}

/** Date-only part of an ISO date-time ("2026-10-03T07:45:00" → "2026-10-03"). */
export function dateOf(dateTime: IsoDateTime): IsoDate {
  const t = dateTime.indexOf("T");
  return t < 0 ? dateTime : dateTime.slice(0, t);
}

export function isBefore(a: IsoDate, b: IsoDate): boolean {
  return epochDay(a) < epochDay(b);
}

/** Add whole days to an ISO date. */
export function plusDays(date: IsoDate, days: number): IsoDate {
  const d = new Date((epochDay(date) + days) * 86_400_000);
  return isoDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function isoDate(year: number, month: number, day: number): IsoDate {
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** Fixed-width ISO date-time, seconds always present. */
export function isoDateTime(date: IsoDate, hour: number, minute: number, second = 0): IsoDateTime {
  return `${date}T${pad(hour, 2)}:${pad(minute, 2)}:${pad(second, 2)}`;
}

/** Compare two ISO date-times lexically (fixed width makes this chronological). */
export function compareDateTime(a: IsoDateTime, b: IsoDateTime): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, "0");
}
